import { BehaviorSubject, filter, Subject } from 'rxjs';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { environment } from '../../environments/environment';
import { SharedSessionAuthService } from './shared-session-auth.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranscriptStreamService } from './transcript/transcript-stream.service';

export enum QRWebsocketState {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

export const QRWebsocketEvents = {
  PING: 'ping',
  PONG: 'pong',
  QR_USER_CONNECTED: 'qr_user.connected',
  QR_USER_DISCONNECTED: 'qr_user.disconnected',
  GUEST_FEATURES_TRANSLATIONS: 'guest.features.translations',
  GUEST_FEATURES_TTS: 'guest.features.tts',
  GUEST_TRANSLATIONS_SUPPORTED_LANGUAGES:
    'guest.translations.supported_languages',
  GUEST_TARGET_LANGUAGES_SET: 'guest.target_languages.set',
  SPEECH_SDK_RECOGNITION_LANGUAGE_UPDATED:
    'speech_sdk.recognition_language.updated',
  SPEECH_SDK_RECOGNITION_LANGUAGE_RESULT:
    'speech_sdk.recognition_language.result',
  GUEST_TEXT_BEHAVIOR: 'guest.text_behavior',
  GUEST_VIEW_APPEARANCE_UPDATED: 'guest.view.appearance.updated',
  SESSION_ENDED_NOTIFICATION: 'session.ended.notification',
  TEXT_BEHAVIOR_UPDATED: /\.text_behavior_updated$/,
  TTS_SUBSCRIBE: 'tts.read_along.subscribe',
  TTS_UNSUBSCRIBE: 'tts.read_along.unsubscribe',
  TTS_AUDIO_INIT: 'tts.read_along.audio_init',
} as const;

export type QRWebsocketEventType =
  (typeof QRWebsocketEvents)[keyof typeof QRWebsocketEvents];

export interface QRWebsocketPayload {
  event: string;
  created_at?: string;
  payload?: any;
}

/**
 * TTS audio init envelope sent by the proxy on the same WS before the
 * binary media segments start arriving. After the migration off raw Opus,
 * the wire format is AAC-LC packaged in fragmented MP4 (fMP4 / CMAF) so
 * the browser's native HTMLMediaElement + (Managed)MediaSource pipeline
 * can play it back — which is the only path that survives screen lock on
 * iOS Safari and background tabs on Android Chrome.
 *
 * Field semantics:
 *  - `codec`: RFC 6381 codec string (e.g. `"mp4a.40.2"` for AAC-LC). The
 *    client constructs the SourceBuffer MIME type as
 *    `audio/mp4; codecs="${codec}"`.
 *  - `sample_rate` / `channels`: informational; the fMP4 init segment is
 *    authoritative for the decoder configuration.
 *  - `sample_format`: legacy field kept for protocol compatibility; not
 *    used by the MSE pipeline.
 *  - `head_b64`: base64-encoded fMP4 initialization segment (ftyp + moov
 *    boxes). Must be appended to the SourceBuffer before any media
 *    segments. Required for AAC-in-fMP4; was the OpusHead description in
 *    the old contract.
 *
 * Subsequent binary WS frames are fMP4 media segments (moof + mdat
 * boxes), one segment per frame, fed to `SourceBuffer.appendBuffer`.
 */
export interface TtsAudioInit {
  type: 'audio';
  codec: string;
  sample_rate: number;
  channels: number;
  sample_format: string;
  /** Base64-encoded fMP4 init segment (ftyp + moov). */
  head_b64?: string;
}

/**
 * WebSocket service specifically for QR authenticated users
 * Handles connection, reconnection, and message handling
 */
@Injectable({
  providedIn: 'root',
})
export class QRWebsocketService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly transcriptStream = inject(TranscriptStreamService);
  private readonly sharedAuth = inject(SharedSessionAuthService);
  private readonly websocketServer: string = environment.speaksee.websocketUrl;

  // Deserializer routes binary frames and TTS init envelopes to their own subjects
  // and returns null in those cases, so subscribers of socket$ only see payloads.
  private socket$: WebSocketSubject<QRWebsocketPayload | null> | null = null;
  private userId: string = '';
  private jwt: string = '';
  private uuid: string | null = null;

  // State management
  private readonly state = new BehaviorSubject<QRWebsocketState>(
    QRWebsocketState.CLOSED
  );
  public readonly state$ = this.state.asObservable();

  // Message streams
  public readonly incomingMessages = new Subject<QRWebsocketPayload>();
  public readonly outgoingMessages = new Subject<QRWebsocketPayload>();
  /** TTS init envelopes (one per active subscription, may resend if Opus header arrives late). */
  public readonly audioInit$ = new Subject<TtsAudioInit>();
  /** Raw opus packets, in arrival order. */
  public readonly audioFrames$ = new Subject<ArrayBuffer>();

  // Retry configuration
  private readonly MAX_RECONNECT_ATTEMPTS = 7;
  private readonly STABLE_CONNECTION_THRESHOLD = 5000; // 5 seconds
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private stableConnectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManualDisconnect = false;

  // Close event codes for debugging
  private readonly closeEventCodes: Record<number, string> = {
    1000: 'Normal closure',
    1001: 'Going away',
    1002: 'Protocol error',
    1003: 'Unsupported data',
    1006: 'Abnormal closure',
    1007: 'Invalid frame payload data',
    1008: 'Policy violation',
    1009: 'Message too big',
    1010: 'Missing extension',
    1011: 'Internal error',
    1015: 'TLS handshake fail',
  };

  constructor() {
    this.outgoingMessages
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((message) => this.sendMessage(message));
  }

  /**
   * Connect to the QR websocket server
   * @param jwt JWT access token
   * @param uuid Optional device UUID (from route params when scanning QR)
   */
  connect(jwt: string, uuid: string): void {
    if (this.socket$ && this.state.value === QRWebsocketState.OPEN) {
      console.warn('[WebSocket] Already connected');
      return;
    }

    this.jwt = jwt;
    this.uuid = uuid || null;
    this.isManualDisconnect = false;

    const params = new URLSearchParams({
      uuid: this.uuid!, // UUID is now required for connection
      jwt: this.jwt,
      client_type: 'guest',
    });

    const url = `${this.websocketServer}?${params.toString()}`;

    this.state.next(QRWebsocketState.CONNECTING);

    this.socket$ = webSocket<QRWebsocketPayload | null>({
      url,
      binaryType: 'arraybuffer',
      // Binary frames go to audioFrames$, JSON envelopes without an `event` field go to audioInit$.
      deserializer: (e: MessageEvent) => {
        if (e.data instanceof ArrayBuffer) {
          this.audioFrames$.next(e.data);
          return null;
        }
        const parsed = JSON.parse(e.data);
        if (parsed?.event === QRWebsocketEvents.TTS_AUDIO_INIT) {
          this.audioInit$.next(parsed.payload as TtsAudioInit);
          return null;
        }
        return parsed as QRWebsocketPayload;
      },
      openObserver: {
        next: () => this.handleConnectionOpened(),
      },
      closeObserver: {
        next: (event: CloseEvent) => this.handleConnectionClosed(event),
      },
    });

    this.socket$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((m): m is QRWebsocketPayload => m !== null)
      )
      .subscribe({
        next: (message) => this.handleIncomingMessage(message),
        error: (error) => this.handleError(error),
      });
  }

  /**
   * Disconnect from the websocket
   */
  disconnect(): void {
    if (!this.socket$) {
      console.warn('[WebSocket] Not connected');
      return;
    }

    this.isManualDisconnect = true;
    this.clearTimeouts();

    this.state.next(QRWebsocketState.CLOSING);

    this.socket$.complete();
    this.socket$ = null;
    this.state.next(QRWebsocketState.CLOSED);
    this.reconnectAttempts = 0;
  }

  /**
   * Check if websocket is connected
   */
  isConnected(): boolean {
    return this.state.value === QRWebsocketState.OPEN;
  }

  /**
   * Get current connection state
   */
  getState(): QRWebsocketState {
    return this.state.value;
  }

  /**
   * Handle connection opened
   */
  private handleConnectionOpened(): void {
    this.state.next(QRWebsocketState.OPEN);
    this.reconnectAttempts = 0;

    this.clearTimeouts();

    // Reset retry counter after stable connection
    this.stableConnectionTimeout = setTimeout(() => {
      this.reconnectAttempts = 0;
    }, this.STABLE_CONNECTION_THRESHOLD);

    if (this.uuid) {
      this.transcriptStream.register(this.uuid, this.incomingMessages);
    }
  }

  /**
   * Handle connection closed
   */
  private handleConnectionClosed(event: CloseEvent): void {
    if (this.uuid) {
      this.transcriptStream.unregister(this.uuid);
    }

    const reason = this.closeEventCodes[event.code] || 'Unknown reason';
    console.warn(`[WebSocket] Connection closed: ${event.code} - ${reason}`);

    this.socket$ = null;
    this.state.next(QRWebsocketState.CLOSED);
    this.clearTimeouts();

    // On JWT/policy failure from the server, attempt token renewal then reconnect
    // (only when a shared session is active and we didn't initiate the close ourselves).
    if (event.code === 1008 && !this.isManualDisconnect && this.sharedAuth.isActive()) {
      void this.renewAndReconnect();
      return;
    }

    // Don't reconnect on normal closure (1000), an unauthenticated 1008 (an active shared
    // session's 1008 is handled above via renewAndReconnect), or a manual disconnect.
    if (
      event.code !== 1000 &&
      event.code !== 1008 &&
      !this.isManualDisconnect
    ) {
      this.scheduleReconnect();
    }
  }

  /**
   * Handle incoming messages
   */
  private handleIncomingMessage(message: QRWebsocketPayload): void {
    switch (message.event) {
      case QRWebsocketEvents.PING:
        this.sendPong();
        this.incomingMessages.next(message);
        break;
      default:
        this.incomingMessages.next(message);
        break;
    }
  }

  /**
   * Handle websocket errors
   */
  private handleError(error: any): void {
    console.error('[WebSocket] Error:', error);
    this.state.next(QRWebsocketState.CLOSED);
  }

  /**
   * Send a message through the websocket
   * @returns true if the message was handed to the socket, false if dropped
   */
  private sendMessage(message: QRWebsocketPayload): boolean {
    if (!this.socket$ || this.state.value !== QRWebsocketState.OPEN) {
      console.warn('[WebSocket] Cannot send message: not connected', message);
      return false;
    }

    this.socket$.next(message);
    return true;
  }

  /**
   * Send PONG response to server
   */
  private sendPong(): void {
    if (this.socket$ && this.state.value === QRWebsocketState.OPEN) {
      this.socket$.next({
        event: QRWebsocketEvents.PONG,
        created_at: new Date().toISOString(),
      });
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      return;
    }

    this.reconnectAttempts++;
    // Exponential backoff: 2^attempt seconds, capped at 60 seconds
    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts - 1),
      60000
    );

    this.reconnectTimeout = setTimeout(() => {
      this.reconnect();
    }, delay);
  }

  /**
   * Attempt to reconnect, preferring the freshest token for active shared sessions.
   */
  private reconnect(): void {
    if (!this.uuid) return;
    const jwt = this.sharedAuth.isActive()
      ? (this.sharedAuth.getAccessToken() ?? this.jwt)
      : this.jwt;
    if (!jwt) return;
    this.connect(jwt, this.uuid);
  }

  /**
   * Renew the shared-session token then reconnect with the fresh one.
   * Called when the server closes with 1008 (JWT/policy violation) while a
   * shared session is active.
   */
  private async renewAndReconnect(): Promise<void> {
    if (!this.uuid) return;
    const ok = await this.sharedAuth.ensureValidToken();
    const fresh = this.sharedAuth.getAccessToken();
    if (ok && fresh) {
      this.connect(fresh, this.uuid);
    }
  }

  /**
   * Clear all timeouts
   */
  private clearTimeouts(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.stableConnectionTimeout) {
      clearTimeout(this.stableConnectionTimeout);
      this.stableConnectionTimeout = null;
    }
  }

  /**
   * Send tts.read_along.subscribe over the existing guest WS. The proxy
   * resolves device_uuid to the active producer's session id internally.
   * Audio metadata arrives on `audioInit$`, opus frames on `audioFrames$`.
   */
  sendTtsSubscribe(lang: string): boolean {
    if (!this.uuid) {
      console.warn(
        '[WebSocket] Cannot subscribe to TTS read-along: no device UUID'
      );
      return false;
    }
    return this.sendMessage({
      event: QRWebsocketEvents.TTS_SUBSCRIBE,
      payload: { device_uuid: this.uuid, lang },
    });
  }

  /** Send tts.read_along.unsubscribe to tear down the active stream. */
  sendTtsUnsubscribe(): void {
    this.sendMessage({ event: QRWebsocketEvents.TTS_UNSUBSCRIBE });
  }

  /**
   * Send guest target languages
   * @param languages List of BCP 47 language codes
   * @param inputUuid Audio input UUID (mic_id) - only for microphone kit users, omit for soundshape
   */
  setGuestTargetLanguages(languages: string[], inputUuid?: string): void {
    if (!this.uuid) {
      return;
    }

    const payload: {
      device_uuid: string;
      input_uuid?: string;
      languages: string[];
    } = {
      device_uuid: this.uuid,
      languages,
    };

    // Only include input_uuid for microphone kit users
    if (inputUuid) {
      payload.input_uuid = inputUuid;
    }

    this.sendMessage({
      event: QRWebsocketEvents.GUEST_TARGET_LANGUAGES_SET,
      payload,
    });
  }
}
